import { RootState } from 'app/store/rootReducer';
import { createAsyncThunk, createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  AddMerchantSpendRuleInput,
  HNCreateAmountLimitSpendRuleInput,
  HNCreateMerchantCategorySpendRuleInput,
  HNCreateMerchantCountrySpendRuleInput,
  HNCreateVelocityRuleInput,
  HNMerchantSpendRule,
  HNUpdateMerchantCategorySpendRuleInput,
  HNVelocityRule,
  MerchantSpendRule,
  UpdateMerchantSpendRuleInput,
} from 'API';
import { highnoteService } from 'app/services/highnote.service';
import { cardService } from 'app/services/card.service';

// Timeout error message constant
export const SPEND_RULE_TIMEOUT_MESSAGE =
  'Your changes are being processed. This may take a moment. Please refresh the page in a few seconds to see the updates.';

// Timeout error marker used to identify timeout errors across the Redux serialization boundary
export const TIMEOUT_ERROR_MARKER = '[TIMEOUT]';

// Utility function to detect timeout errors
function isTimeoutError(error: any, duration: number): boolean {
  const isTimeoutByDuration = duration >= 28000 && duration <= 32000;

  // Safely normalize the error message to avoid crashes on undefined/non-string values
  const msg = String(error?.message ?? '').toLowerCase();
  const isTimeoutByMessage = msg.includes('timeout') || msg.includes('timed out');

  const isTimeoutByErrorType =
    error?.errorType === 'TimeoutError' ||
    error?.errorType === 'LambdaTimeout' ||
    error?.errorType === 'Task timed out';

  return isTimeoutByDuration || isTimeoutByMessage || isTimeoutByErrorType;
}

const spendRulesEntityAdapter = createEntityAdapter<MerchantSpendRule>();

const initialState = spendRulesEntityAdapter.getInitialState({
  loading: false,
  nextToken: undefined as string | undefined,
  selectedSpendRuleId: undefined as string | undefined,
  spendRulesList: {} as Record<string, HNMerchantSpendRule>,
  copyFromSpendRuleId: undefined as string | undefined,
});

export type SpendRulesState = typeof initialState;

export interface GetSpendRulesArgs {
  paidolId: string;
  nextToken?: string;
  limit?: number;
  shouldLoadDetails?: boolean;
}

export interface GetSpendRuleByIdArgs {
  ruleId: string;
}

export interface UpdateSpendRuleArgs {
  merchantCategoryInput?: HNUpdateMerchantCategorySpendRuleInput;
  merchantCountryInput?: HNUpdateMerchantCategorySpendRuleInput;
  updateMerchantSpendRuleInput: UpdateMerchantSpendRuleInput;
}

export const getMerchantSpendRules = createAsyncThunk(
  'cards/spend-rules/getMerchantSpendRules',
  async ({ paidolId }: GetSpendRulesArgs) => {
    return cardService.listMerchantSpendRulesForPaidol({ input: { paidol_id: paidolId } });
  }
);

export const getSpendRuleById = createAsyncThunk(
  'cards/spend-rules/getSpendRuleById',
  async ({ ruleId }: GetSpendRuleByIdArgs) => {
    return (await highnoteService.hnGetSpendRuleById({ ruleId })) as HNMerchantSpendRule;
  }
);

export const getVelocityRuleById = createAsyncThunk(
  'cards/spend-rules/getVelocityRuleById',
  async ({ ruleId }: GetSpendRuleByIdArgs) => {
    return (await highnoteService.hnGetVelocityRuleById({ ruleId })) as HNVelocityRule;
  }
);

export const newMerchantSpendRule = createAsyncThunk(
  'cards/spend-rules/newMerchantSpendRule',
  async (data: AddMerchantSpendRuleInput) => {
    const startTime = Date.now();

    try {
      return await cardService.addMerchantSpendRule({ input: data });
    } catch (error: any) {
      const duration = Date.now() - startTime;

      if (isTimeoutError(error, duration)) {
        const timeoutError = new Error(
          `${TIMEOUT_ERROR_MARKER} ${error?.message || SPEND_RULE_TIMEOUT_MESSAGE}`
        );
        throw timeoutError;
      }

      throw error;
    }
  }
);

//update spend rule (updateMerchantSpendRuleInput)
export const updateSpendRule = createAsyncThunk<MerchantSpendRule | undefined, UpdateSpendRuleArgs>(
  'cards/spend-rules/updateSpendRule',
  async ({ updateMerchantSpendRuleInput }): Promise<MerchantSpendRule | undefined> => {
    const startTime = Date.now();

    try {
      const response = await cardService.editMerchantSpendRule({
        input: updateMerchantSpendRuleInput,
      });

      // Detect the MerchantSpendRuleError arm of the union structurally: it carries an
      // `errors` field, MerchantSpendRule does not. (CDK codegen strips __typename from the
      // types, so we discriminate by shape rather than by __typename.)
      if (response && typeof response === 'object' && 'errors' in response) {
        const errorResponse = response as { errors: any };
        if (errorResponse.errors) {
          console.error('[SpendRule Update] Error field in response:', errorResponse.errors);
          throw new Error(String(errorResponse.errors));
        }
      }

      // Check if response looks like a successful MerchantSpendRule response
      // Since __typename might be missing, we check for the presence of expected fields
      if (response && typeof response === 'object' && 'id' in response && response.id) {
        // Bridge the CDK MerchantSpendRule (nullable createdAt/updatedAt/etc.) to the slice's
        // API MerchantSpendRule that the entity adapter stores. Runtime data is complete; the
        // difference is nullability in the generated types.
        const merchantSpendRule = {
          ...response,
          __typename: 'MerchantSpendRule' as const,
          maximumAmount: response.maximumAmount
            ? {
                __typename: 'HNAmount' as const,
                value: response.maximumAmount.value,
                currencyCode: response.maximumAmount.currencyCode,
                decimalPlaces: response.maximumAmount.decimalPlaces,
              }
            : null,
          monthlyLimitAmount: response.monthlyLimitAmount
            ? {
                __typename: 'HNAmount' as const,
                value: response.monthlyLimitAmount.value,
                currencyCode: response.monthlyLimitAmount.currencyCode,
                decimalPlaces: response.monthlyLimitAmount.decimalPlaces,
              }
            : null,
        } as unknown as MerchantSpendRule;
        return merchantSpendRule;
      }

      // If we get here, the response is unexpected - throw an error
      console.error('[SpendRule Update] Unexpected response type:', response);
      throw new Error('Unexpected response type from editMerchantSpendRule mutation');
    } catch (error: any) {
      const duration = Date.now() - startTime;

      if (isTimeoutError(error, duration)) {
        const timeoutError = new Error(
          `${TIMEOUT_ERROR_MARKER} ${error?.message || SPEND_RULE_TIMEOUT_MESSAGE}`
        );
        throw timeoutError;
      }

      throw error;
    }
  }
);

export const removeSpendRule = createAsyncThunk('cards/spend-rules/removeSpendRule', async (id: string) => {
  const response = await cardService.removeMerchantSpendRule({ input: id });

  // Check if the response indicates success
  // For this API, null statusCode and body indicate success
  if (response && response.statusCode === null && response.body === null) {
    // Return the ID of the removed spend rule for optimistic updates
    return id;
  }

  // If statusCode is 200, that's also success
  if (response && response.statusCode === 200) {
    return id;
  }

  // If statusCode is not null/200, throw an error with the body message
  if (response && response.statusCode && response.statusCode !== 200) {
    throw new Error(response.body || `Failed to remove spend rule (Status: ${response.statusCode})`);
  }

  // Fallback error if response structure is unexpected
  throw new Error('Failed to remove spend rule');
});

export const createMerchantCategorySpendRule = createAsyncThunk(
  'spendRules/createMerchantCategorySpendRule',
  async (merchantCategoryInput: HNCreateMerchantCategorySpendRuleInput) => {
    return highnoteService.hnCreateMerchantCategorySpendRule(merchantCategoryInput);
  }
);

export const createMerchantCountrySpendRule = createAsyncThunk(
  'spendRules/createMerchantCountrySpendRule',
  async (merchantCountryInput: HNCreateMerchantCountrySpendRuleInput) => {
    return highnoteService.hnCreateMerchantCountrySpendRule(merchantCountryInput);
  }
);

export const createAmountLimitSpendRule = createAsyncThunk(
  'spendRules/createAmountLimitSpendRule',
  async (amountLimitInput: HNCreateAmountLimitSpendRuleInput) => {
    return highnoteService.hnCreateAmountLimitSpendRule(amountLimitInput);
  }
);

export const createVelocitySpendRule = createAsyncThunk(
  'spendRules/createVelocitySpendRule',
  async (input: HNCreateVelocityRuleInput) => {
    return highnoteService.hnCreateVelocityRule(input);
  }
);

const spendRuleSlice = createSlice({
  name: 'cards/spend-rules',
  initialState,
  reducers: {
    resetSpendRulesSlice: () => initialState,
    setSelectedSpendRuleId: (state, action) => {
      state.selectedSpendRuleId = action.payload;
    },
    setCopyFromSpendRuleId: (state, action) => {
      state.copyFromSpendRuleId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMerchantSpendRules.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getSpendRuleById.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getMerchantSpendRules.fulfilled, (state, action) => {
      if (action.payload) {
        spendRulesEntityAdapter.setAll(state, action.payload as MerchantSpendRule[]);
      } else {
        spendRulesEntityAdapter.setAll(state, []);
      }
      state.loading = false;
      state.nextToken = undefined;
    });
    builder.addCase(getSpendRuleById.fulfilled, (state, action) => {
      if (action.payload?.id) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.spendRulesList.set(action.payload.id, action.payload);
      }
    });
    builder.addCase(getVelocityRuleById.fulfilled, (state, action) => {
      if (action.payload?.id) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.spendRulesList.set(action.payload.id, action.payload);
      }
    });
    builder.addCase(updateSpendRule.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateSpendRule.fulfilled, (state, action) => {
      const updatedRule = action.payload;

      if (updatedRule?.id) {
        spendRulesEntityAdapter.upsertOne(state, updatedRule);

        state.spendRulesList = {
          ...state.spendRulesList,
          [updatedRule.id]: updatedRule as unknown as HNMerchantSpendRule,
        };
      }

      state.loading = false;
    });
    builder.addCase(updateSpendRule.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(removeSpendRule.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(removeSpendRule.fulfilled, (state, action) => {
      const removedRuleId = action.payload;

      // Don't immediately remove from state - let the backend refresh handle it
      // This ensures we don't have conflicts between optimistic updates and backend data

      state.loading = false;
      state.selectedSpendRuleId = undefined;
    });
    builder.addCase(removeSpendRule.rejected, (state, action) => {
      state.loading = false;
      state.selectedSpendRuleId = undefined;
    });
    builder.addCase(newMerchantSpendRule.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(newMerchantSpendRule.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(newMerchantSpendRule.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addMatcher(
      isAnyOf(
        createMerchantCategorySpendRule.pending,
        createMerchantCountrySpendRule.pending,
        createAmountLimitSpendRule.pending
      ),
      (state) => {
        state.loading = true;
      }
    );
    builder.addMatcher(
      isAnyOf(
        createMerchantCategorySpendRule.fulfilled,
        createMerchantCountrySpendRule.fulfilled,
        createAmountLimitSpendRule.fulfilled
      ),
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addMatcher(
      isAnyOf(
        createMerchantCategorySpendRule.rejected,
        createMerchantCountrySpendRule.rejected,
        createAmountLimitSpendRule.rejected
      ),
      (state, action) => {
        state.loading = false;
      }
    );
  },
});

export const { resetSpendRulesSlice, setSelectedSpendRuleId, setCopyFromSpendRuleId } =
  spendRuleSlice.actions;

export const selectSpendRulesSlice = (state: RootState) => state.cards?.spendRules ?? initialState;

export const selectSelectedSpendRule = (state: RootState) => {
  if (state.cards?.spendRules.selectedSpendRuleId) {
    return selectBySpendRuleId(state, state.cards?.spendRules.selectedSpendRuleId);
  }

  return undefined;
};
export const selectCopyFromSpendRule = (state: RootState) => {
  if (state.cards?.spendRules.copyFromSpendRuleId) {
    return selectBySpendRuleId(state, state.cards?.spendRules.copyFromSpendRuleId);
  }
  return undefined;
};
export const { selectAll: selectAllSpendRules, selectById: selectBySpendRuleId } =
  spendRulesEntityAdapter.getSelectors(selectSpendRulesSlice);

export default spendRuleSlice.reducer;
